import React, { useState } from "react";
import api from "@/lib/api";
import { Plus, X, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal form for coaches (and admins) to add a new exercise into the library.
 * Backend endpoints:
 *   POST /api/exercises/custom           → create exercise doc
 *   POST /api/exercises/custom/{id}/image → optional hero image upload
 *
 * Custom exercises are private to the coach (and their clients) until an
 * admin promotes them via POST /api/exercises/custom/{id}/promote.
 */
export default function CustomExerciseModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    muscles: "",
    equipment: "dumbbells",
    difficulty: "beginner",
    steps: "",
    cues: "",
    video_url: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        muscles: form.muscles.split(",").map((m) => m.trim()).filter(Boolean),
        equipment: form.equipment,
        difficulty: form.difficulty,
        steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean),
        cues: form.cues.split("\n").map((s) => s.trim()).filter(Boolean),
        video_url: form.video_url.trim() || null,
      };
      const r = await api.post("/exercises/custom", payload);
      const created = r.data;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/exercises/custom/${created.id}/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.warning("Exercise saved but image upload failed");
        }
      }
      toast.success(`"${created.name}" added to your library`);
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

  return (
    <div
      className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4"
      data-testid="custom-ex-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-obsidian-800 border-b border-obsidian-600 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">New exercise</div>
              <h3 className="font-heading text-2xl uppercase">Add to library</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
            data-testid="custom-ex-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="overline mb-1">Name *</div>
            <input
              data-testid="custom-ex-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Landmine Row"
              className={inputCls}
              required
            />
          </label>

          <label className="block">
            <div className="overline mb-1">Short description</div>
            <textarea
              data-testid="custom-ex-description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={2}
              placeholder="Explosive unilateral back builder."
              className={inputCls}
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block md:col-span-1">
              <div className="overline mb-1">Muscles</div>
              <input
                data-testid="custom-ex-muscles"
                value={form.muscles}
                onChange={(e) => update("muscles", e.target.value)}
                placeholder="lats, mid-back, biceps"
                className={inputCls}
              />
            </label>
            <label className="block">
              <div className="overline mb-1">Equipment</div>
              <select
                data-testid="custom-ex-equipment"
                value={form.equipment}
                onChange={(e) => update("equipment", e.target.value)}
                className={inputCls}
              >
                <option value="barbell">Barbell</option>
                <option value="dumbbells">Dumbbells</option>
                <option value="bodyweight">Bodyweight</option>
                <option value="cable">Cable</option>
                <option value="machine">Machine</option>
                <option value="kettlebell">Kettlebell</option>
                <option value="bands">Bands</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <div className="overline mb-1">Difficulty</div>
              <select
                data-testid="custom-ex-difficulty"
                value={form.difficulty}
                onChange={(e) => update("difficulty", e.target.value)}
                className={inputCls}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </label>
          </div>

          <label className="block">
            <div className="overline mb-1">Step-by-step (one per line)</div>
            <textarea
              data-testid="custom-ex-steps"
              value={form.steps}
              onChange={(e) => update("steps", e.target.value)}
              rows={4}
              placeholder={"Load the landmine end.\nHinge, grip barbell.\nRow to hip, squeeze scap."}
              className={inputCls}
            />
          </label>

          <label className="block">
            <div className="overline mb-1">Form cues (one per line)</div>
            <textarea
              data-testid="custom-ex-cues"
              value={form.cues}
              onChange={(e) => update("cues", e.target.value)}
              rows={3}
              placeholder={"Elbow tight to torso\nDrive with lats not biceps"}
              className={inputCls}
            />
          </label>

          <label className="block">
            <div className="overline mb-1">Video URL (YouTube / Vimeo · optional)</div>
            <input
              data-testid="custom-ex-video-url"
              value={form.video_url}
              onChange={(e) => update("video_url", e.target.value)}
              placeholder="https://youtube.com/watch?v=…"
              className={inputCls}
            />
          </label>

          <div>
            <div className="overline mb-1">Hero image (optional · 8MB max)</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="custom-ex-file"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="ghost-btn px-3 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
                data-testid="custom-ex-pick-file"
              >
                <Upload className="h-3 w-3" />
                {file ? "Change file" : "Choose image"}
              </button>
              {file && (
                <span className="text-xs text-zinc-400 truncate">{file.name}</span>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-obsidian-800 border-t border-obsidian-600 px-5 py-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="ghost-btn px-4 py-2 text-xs uppercase tracking-widest"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="custom-ex-save"
            className="blaze-btn px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            {saving ? "Saving…" : "Save exercise"}
          </button>
        </div>
      </form>
    </div>
  );
}
